import type { GeoRegion } from '~/utils/geo';
import type { GeoPreferenceTargetInput } from '~/utils/geoRedirect';

import { useGlobalClusters } from '~/composables/useGlobalClusters';
import { useAuthStore } from '~/stores/auth';
import { useConfigStore } from '~/stores/config';
import {
  GEO_ROUTING_DISMISSED_COOKIE,
  PHOENIX_GEO_STATE,
  PHOENIX_IS_BOT_STATE,
} from '~/utils/geo';

/**
 * Assembles the route-independent part of the geo policy input
 * (`resolveGeoPreferenceTarget` / `isGeoPromptEligible`) from its canonical
 * sources — the ONE place that knows which cookie, which auth signal, and
 * which cluster state feed the policy, so the SSR middleware and the client
 * surfaces can't drift apart on input assembly. Consumers spread the bag and
 * add the route's `path`/`query`.
 *
 * Reads `authStore.user` directly rather than the `useAuth()` facade: the
 * policy needs one boolean, and this runs on every SSR request via the
 * middleware — the facade builds a large computed/watch graph per call.
 */
export const useGeoPreferenceTargetInput = () => {
  const authStore = useAuthStore();
  const configStore = useConfigStore();
  const { code } = useCurrentCluster();
  const { clusters, currentCluster } = useGlobalClusters();
  const preference = useRegionPreference();
  const dismissed = useCookie<boolean>(GEO_ROUTING_DISMISSED_COOKIE, { sameSite: 'lax', secure: true, default: () => false });
  const isBot = useState<boolean>(PHOENIX_IS_BOT_STATE, () => false);
  const detectedGeo = useState<GeoRegion | null>(PHOENIX_GEO_STATE, () => null);

  const input = computed<Omit<GeoPreferenceTargetInput, 'path' | 'query'>>(() => ({
    preference: preference.value,
    dismissed: dismissed.value,
    isAuthenticated: !!authStore.user,
    isSchools: configStore.isSchoolsCluster,
    isBot: isBot.value,
    clusterCode: code.value,
    clusters: clusters.value,
    currentGeo: currentCluster.value?.geo ?? null,
  }));

  return { input, preference, dismissed, detectedGeo, currentCluster };
};
